import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import type { Company } from '@/types';

// ===========================================
// Types
// ===========================================

export interface DuplicateGroup {
  /** Normalized name used for grouping */
  key: string;
  /** Display name (from first company) */
  displayName: string;
  /** Companies in this group */
  companies: Company[];
  /** Similarity type: 'exact' or 'similar' */
  matchType: 'exact' | 'similar';
}

export interface MergeSummary {
  contacts: number;
  projects: number;
  visits: number;
  outboundReferrals: number;
  partnerTokens: number;
  partnerSessions: number;
  evidence: number;
  partnerNotes: number;
  portalProjects: number;
}

export interface MergeResult {
  success: boolean;
  summary: MergeSummary;
  keptCompanyName: string;
  retiredCompanyName: string;
}

// ===========================================
// Fuzzy Matching Utilities
// ===========================================

/**
 * Normalize a company name for exact duplicate detection
 * Strips common suffixes, punctuation, extra whitespace
 */
export function normalizeName(name: string): string {
  return name
    .toLowerCase()
    .trim()
    // Remove common business suffixes
    .replace(/\b(inc|llc|llp|ltd|corp|co|company|group|services|enterprises?)\b\.?/gi, '')
    // Remove punctuation
    .replace(/[.,\-_'"()&]/g, ' ')
    // Collapse whitespace
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Calculate similarity between two strings using bigram overlap (Dice coefficient)
 * Returns 0-1 where 1 is identical
 */
export function diceSimilarity(a: string, b: string): number {
  if (a === b) return 1;
  if (a.length < 2 || b.length < 2) return 0;

  const bigramsA = new Set<string>();
  for (let i = 0; i < a.length - 1; i++) {
    bigramsA.add(a.substring(i, i + 2));
  }

  const bigramsB = new Set<string>();
  for (let i = 0; i < b.length - 1; i++) {
    bigramsB.add(b.substring(i, i + 2));
  }

  let intersection = 0;
  for (const bigram of bigramsA) {
    if (bigramsB.has(bigram)) intersection++;
  }

  return (2 * intersection) / (bigramsA.size + bigramsB.size);
}

/**
 * Check if one name contains the other (for partial matches)
 */
function containsMatch(a: string, b: string): boolean {
  const shorter = a.length <= b.length ? a : b;
  const longer = a.length > b.length ? a : b;
  // Only match if shorter is at least 4 chars to avoid false positives
  return shorter.length >= 4 && longer.includes(shorter);
}

// ===========================================
// Duplicate Detection
// ===========================================

export const SIMILARITY_THRESHOLD = 0.7; // Dice coefficient threshold for "similar"

/**
 * Find duplicate and similar companies
 * Groups by:
 * 1. Exact normalized name match
 * 2. High similarity score (>= 0.7)
 * 3. One name contains the other
 */
export function findDuplicateCompanies(companies: Company[]): DuplicateGroup[] {
  const groups: DuplicateGroup[] = [];
  const assignedIds = new Set<string>();

  // Pass 1: Exact normalized name matches
  const normalizedMap = new Map<string, Company[]>();
  for (const company of companies) {
    const key = normalizeName(company.name);
    if (!normalizedMap.has(key)) {
      normalizedMap.set(key, []);
    }
    normalizedMap.get(key)!.push(company);
  }

  for (const [key, group] of normalizedMap.entries()) {
    if (group.length >= 2) {
      groups.push({
        key,
        displayName: group[0].name,
        companies: group.sort((a, b) => a.name.localeCompare(b.name)),
        matchType: 'exact',
      });
      group.forEach((c) => assignedIds.add(c.id));
    }
  }

  // Pass 2: Fuzzy similarity among remaining companies
  const remaining = companies.filter((c) => !assignedIds.has(c.id));
  const normalizedRemaining = remaining.map((c) => ({
    company: c,
    normalized: normalizeName(c.name),
  }));

  for (let i = 0; i < normalizedRemaining.length; i++) {
    if (assignedIds.has(normalizedRemaining[i].company.id)) continue;

    const similarGroup: Company[] = [normalizedRemaining[i].company];

    for (let j = i + 1; j < normalizedRemaining.length; j++) {
      if (assignedIds.has(normalizedRemaining[j].company.id)) continue;

      const a = normalizedRemaining[i].normalized;
      const b = normalizedRemaining[j].normalized;

      const similarity = diceSimilarity(a, b);
      const contains = containsMatch(a, b);

      if (similarity >= SIMILARITY_THRESHOLD || contains) {
        similarGroup.push(normalizedRemaining[j].company);
        assignedIds.add(normalizedRemaining[j].company.id);
      }
    }

    if (similarGroup.length >= 2) {
      assignedIds.add(normalizedRemaining[i].company.id);
      groups.push({
        key: normalizedRemaining[i].normalized,
        displayName: normalizedRemaining[i].company.name,
        companies: similarGroup.sort((a, b) => a.name.localeCompare(b.name)),
        matchType: 'similar',
      });
    }
  }

  // Sort: exact matches first, then similar; within each, alphabetically
  return groups.sort((a, b) => {
    if (a.matchType !== b.matchType) {
      return a.matchType === 'exact' ? -1 : 1;
    }
    return a.displayName.localeCompare(b.displayName);
  });
}

// ===========================================
// Merge API Call
// ===========================================

/**
 * Call the Cloud Function to merge two companies
 */
export async function mergeCompanies(
  keepCompanyId: string,
  retireCompanyId: string
): Promise<MergeResult> {
  const callable = httpsCallable<
    { keepCompanyId: string; retireCompanyId: string },
    MergeResult
  >(functions, 'crm_mergeCompanies');

  const result = await callable({ keepCompanyId, retireCompanyId });
  return result.data;
}
